import {ConfigObjectType, useApiQuery, useCurrentLocale, ENABLE_PH_REC} from '@wallaby-ui/core';
import {
  Formatters,
  SelectedFilter,
  WallabyCoreSchema,
  formatLabMethodName,
  useAtbList,
  useMedicationAtcs,
  useMedications,
  useOrganizations,
  useSnomedLabels,
} from '@wallaby-ui/document';
import {useCallback, useMemo} from 'react';

import {BIOCHEMISTRY_ROWS, BiochemistryRow, countBiochemistryItems, isBiochemistryRowComplete} from './hospitalizedPatientsBiochemistry';
import {DEFAULT_PATIENTS_FILTERS, PatientsFilters} from './hospitalizedPatientsFilters';
import {
  ACTIVE_INGREDIENT_SLOT_LABELS,
  ACTIVE_INGREDIENT_SLOT_NAMES,
  ATC_SLOT_LABELS,
  ATC_SLOT_NAMES,
  countMedicationConditions,
} from './hospitalizedPatientsMedication';
import {MICROBIOLOGY_ROWS, countMicrobiologyConditions} from './hospitalizedPatientsMicrobiology';
import {formatIcdOptionLabel} from './hospitalizedPatientsOther';

export type SelectOption = {id: string; label: string};
type LabelGetter = (id: unknown) => string;

/** Everything the hospitalizations filter form and its chips need to turn stored ids into labels. */
export type HospitalizedPatientsChipLookups = {
  organizations: {id?: number | string; organizationLongName?: string}[];
  medicationAtcs: {id?: number | string; label?: string}[] | undefined;
  activeIngredientOptions: SelectOption[];
  icdOptions: SelectOption[];
  labMethodOptions: SelectOption[];
  pathogenOptions: SelectOption[];
  resistanceOptions: SelectOption[];
  getOrganizationLabel: LabelGetter;
  getAtcLabel: LabelGetter;
  getActiveIngredientLabel: LabelGetter;
  getPathogenLabel: LabelGetter;
  getResistanceLabel: LabelGetter;
  getIcdLabel: (code: string) => string;
  getLabMethodLabel: (method: string) => string;
};

/**
 * Loads the code lists the hospitalizations filters are rendered with (departments, ATC groups, active
 * ingredients, ICD codes, lab methods, pathogens, antibiotics) and derives the label getters the chips use.
 * Shared by the list page and by every surface that shows a saved filter of the hospitalizationList
 * component as chips (the alert tooltips). The queries are cached by react-query, so a second caller on the
 * same screen costs nothing.
 */
export function useHospitalizedPatientsChipLookups(config: ConfigObjectType): HospitalizedPatientsChipLookups {
  const currentLocale = useCurrentLocale();
  const {data: organizations} = useOrganizations(config);
  const {data: medicationAtcs} = useMedicationAtcs<{id?: number | string; label?: string}>(config);
  const {data: activeIngredients} = useMedications(config, 'ACTIVE_INGREDIENT');
  const activeIngredientOptions = useMemo(() => {
    const collator = new Intl.Collator(currentLocale, {sensitivity: 'base', usage: 'sort', numeric: false});
    return activeIngredients.map((v) => ({id: String(v.id), label: v.name})).sort((a, b) => collator.compare(a.label, b.label));
  }, [activeIngredients, currentLocale]);
  const icdListUrl = config.service.url.icdList;
  const {data: icdCodes} = useApiQuery<{code: string; label?: string | null}[]>([icdListUrl], null, {
    axiosConfig: {url: icdListUrl},
  });
  const icdOptions = useMemo(() => {
    const collator = new Intl.Collator(currentLocale, {sensitivity: 'base', usage: 'sort', numeric: true});
    return (icdCodes ?? [])
      .map((v) => ({id: v.code, label: formatIcdOptionLabel(v.code, v.label)}))
      .sort((a, b) => collator.compare(a.id, b.id));
  }, [icdCodes, currentLocale]);
  const labMethodTypesUrl = `${config.service.url.chartRoot}/supported-event-types-v3`;
  const {data: labMethodTypes} = useApiQuery<WallabyCoreSchema.components['schemas']['LabResultEventTypeV3Dto'][]>(
    [labMethodTypesUrl],
    null,
    {axiosConfig: {url: labMethodTypesUrl}}
  );
  const labMethodOptions = useMemo(() => {
    return (labMethodTypes ?? [])
      .map((type) => ({
        id: type.eventType,
        label: formatLabMethodName({eventType: type.eventType, label: type.label, unit: type.unit}),
      }))
      .sort((a, b) => {
        const technicalA = a.label.startsWith('[') ? 1 : 0;
        const technicalB = b.label.startsWith('[') ? 1 : 0;
        return technicalA - technicalB || a.label.localeCompare(b.label, currentLocale);
      });
  }, [labMethodTypes, currentLocale]);
  // Microbiology box (PDM-2748). Pathogens come from core's finding-code catalogue, narrowed here to the categories
  // lab_result_list can contain - BODY_CELLS findings are dropped there, so offering them would guarantee an empty
  // list. The catalogue label is whatever the NLP stored on first import - English, Czech from the seed list, or for
  // Datlowe's own concepts (999xx) just the code - so both lists are labelled through the SNOMED resource like the
  // Labs and Antibiogram screens, catalogue label last (PDM-2858).
  const pathogenCodesUrl = config.service.url.labResultFindingCodes;
  const {data: pathogenCodes} = useApiQuery<{code: number; category: string; label: string}[]>([pathogenCodesUrl], null, {
    axiosConfig: {url: pathogenCodesUrl},
  });
  const {snomedLabels} = useSnomedLabels(config);
  const pathogenOptions = useMemo(() => {
    const collator = new Intl.Collator(currentLocale, {sensitivity: 'base', usage: 'sort'});
    return (pathogenCodes ?? [])
      .filter((v) => v.category === 'HAI_PATHOGEN' || v.category === 'OTHER_PATHOGEN')
      .map((v) => ({id: String(v.code), label: snomedLabels[v.code] || v.label}))
      .sort((a, b) => collator.compare(a.label, b.label));
  }, [pathogenCodes, snomedLabels, currentLocale]);
  const atbList = useAtbList(config);
  const resistanceOptions = useMemo(() => {
    const collator = new Intl.Collator(currentLocale, {sensitivity: 'base', usage: 'sort'});
    return atbList.map((v) => ({id: String(v.code), label: v.label})).sort((a, b) => collator.compare(a.label, b.label));
  }, [atbList, currentLocale]);
  const getOrganizationLabel = useCallback<LabelGetter>(
    (id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName ?? String(id),
    [organizations]
  );
  const getAtcLabel = useCallback<LabelGetter>(
    (id) => medicationAtcs?.find((v) => String(v.id) === String(id))?.label ?? String(id),
    [medicationAtcs]
  );
  const getActiveIngredientLabel = useCallback<LabelGetter>(
    (id) => activeIngredientOptions.find((v) => String(v.id) === String(id))?.label ?? String(id),
    [activeIngredientOptions]
  );
  const getPathogenLabel = useCallback<LabelGetter>(
    (id) => pathogenOptions.find((v) => v.id === String(id))?.label ?? String(id),
    [pathogenOptions]
  );
  const getResistanceLabel = useCallback<LabelGetter>(
    (id) => resistanceOptions.find((v) => v.id === String(id))?.label ?? String(id),
    [resistanceOptions]
  );
  const getIcdLabel = useCallback((code: string) => icdOptions.find((option) => option.id === code)?.label ?? code, [icdOptions]);
  const getLabMethodLabel = useCallback(
    (method: string) => labMethodOptions.find((option) => option.id === method)?.label ?? method,
    [labMethodOptions]
  );
  return {
    organizations,
    medicationAtcs,
    activeIngredientOptions,
    icdOptions,
    labMethodOptions,
    pathogenOptions,
    resistanceOptions,
    getOrganizationLabel,
    getAtcLabel,
    getActiveIngredientLabel,
    getPathogenLabel,
    getResistanceLabel,
    getIcdLabel,
    getLabMethodLabel,
  };
}

/** A biochemistry row as one chip text: "Potassium (mmol/l) > 6". Empty while the row is incomplete. */
export function getBiochemistryConditionLabel(
  patients: PatientsFilters,
  row: BiochemistryRow,
  formatters: Pick<Formatters, 'tEnum'>,
  getLabMethodLabel: (method: string) => string
): string {
  if (!isBiochemistryRowComplete(patients, row)) return '';
  const method = patients[row.method] ?? '';
  const methodLabel = getLabMethodLabel(method);
  const operator = formatters.tEnum('LabValueOperatorEnum', patients[row.operator]);
  return [methodLabel, operator, patients[row.value]].filter(Boolean).join(' ');
}

/**
 * The filter object the "Active filters" chips are checked against: the occurrence selectors count only once two
 * conditions exist, and an incomplete biochemistry row is no chip at all. The same object decides which chips of a
 * saved filter are shown anywhere else (the alert tooltips), so one rule serves every surface.
 */
export function hospitalizedPatientsTableFiltersForChips(patients: PatientsFilters): Record<string, any> {
  const result: Record<string, any> = {...patients};
  if (countMedicationConditions(patients) < 2) {
    delete result.atcHierarchyMatch;
  }
  if (countBiochemistryItems(patients) < 2) {
    delete result.biochemistryMatch;
  }
  if (countMicrobiologyConditions(patients) < 2) {
    delete result.microbiologyMatch;
  }
  BIOCHEMISTRY_ROWS.filter((row) => !isBiochemistryRowComplete(patients, row)).forEach((row) => {
    delete result[row.method];
    delete result[row.operator];
    delete result[row.value];
  });
  return result;
}

type ChipLookups = Pick<
  HospitalizedPatientsChipLookups,
  | 'getOrganizationLabel'
  | 'getAtcLabel'
  | 'getActiveIngredientLabel'
  | 'getPathogenLabel'
  | 'getResistanceLabel'
  | 'getIcdLabel'
  | 'getLabMethodLabel'
>;

/**
 * The chip definitions of the hospitalizations filters, one per filter, in the field order of the tab panels.
 * Every chip belongs to one filter-panel tab: the tab badges count the chips of their tab, the "Active filters"
 * heading counts them all. Pure: the list page and the alert tooltips call it with their own lookups.
 */
export function buildHospitalizedPatientsSelectedFilters(
  patients: PatientsFilters,
  formatters: Pick<Formatters, 't' | 'tEnum' | 'formatDate'>,
  lookups: ChipLookups,
  enablePhRec: boolean = ENABLE_PH_REC
): SelectedFilter[] {
  const {t, tEnum, formatDate} = formatters;
  const {
    getOrganizationLabel,
    getAtcLabel,
    getActiveIngredientLabel,
    getPathogenLabel,
    getResistanceLabel,
    getIcdLabel,
    getLabMethodLabel,
  } = lookups;
  const withTab = (tab: string, items: SelectedFilter[]): SelectedFilter[] => items.map((v) => ({...v, tab}));
  return [
    ...withTab('general', [
      {
        name: 'patientFullName',
        label: t('filter.patient.fullName'),
        value: patients.patientFullName,
      },
      {
        name: 'patientIdentificationNumber',
        label: t('filter.patient.identificationNumber'),
        value: patients.patientIdentificationNumber,
      },
      {
        name: 'patientSrcId',
        label: t('filter.patient.sourceId'),
        value: patients.patientSrcId,
      },
      {
        name: 'encounterOrganizationIds',
        label: t('filter.patient.admissionDepartment'),
        value: (patients.encounterOrganizationIds ?? []).map(getOrganizationLabel).join(', '),
      },
      {
        name: ['encounterStartedFrom', 'encounterStartedTo', 'encounterFloatingShortcutName'],
        label: t('filter.patient.admissionDate'),
        value: `${formatDate(patients.encounterStartedFrom ?? '')} - ${formatDate(patients.encounterStartedTo ?? '')}`,
      },
      {
        name: 'lastEncounterSubOrganizationIds',
        label: t('filter.patient.currentDepartment'),
        value: (patients.lastEncounterSubOrganizationIds ?? []).map(getOrganizationLabel).join(', '),
      },
      {
        name: ['lastEncounterSubStartedFrom', 'lastEncounterSubStartedTo', 'lastEncounterSubFloatingShortcutName'],
        label: t('filter.patient.transferDate'),
        value: `${formatDate(patients.lastEncounterSubStartedFrom ?? '')} - ${formatDate(patients.lastEncounterSubStartedTo ?? '')}`,
      },
      {
        name: ['riskScoreFrom', 'riskScoreTo'],
        label: t('filter.patient.riskScore'),
        value: `${patients.riskScoreFrom ?? ''} - ${patients.riskScoreTo ?? ''}`,
      },
      {
        name: 'riskFactorTypes',
        label: t('filter.patient.riskFactorTypes'),
        value: (patients.riskFactorTypes ?? []).map((v) => tEnum('RiskFactorTypeEnum', v)).join(', '),
      },
      {
        name: ['riskFactorsCountFrom', 'riskFactorsCountTo'],
        label: t('filter.patient.riskFactors'),
        value: `${patients.riskFactorsCountFrom ?? ''} - ${patients.riskFactorsCountTo ?? ''}`,
      },
      ...(enablePhRec
        ? [
            {
              name: 'pharmaRecommendationRiskinesses',
              label: t('filter.patient.pharmaRecommendationRiskinesses'),
              value: (patients.pharmaRecommendationRiskinesses ?? []).map((v) => tEnum('RiskinessHospitalizationFilter', v)).join(', '),
            },
            {
              name: ['pharmaRecommendationDateFrom', 'pharmaRecommendationDateTo', 'pharmaRecommendationFloatingShortcutName'],
              label: t('filter.patient.pharmaRecommendationDate'),
              value: `${formatDate(patients.pharmaRecommendationDateFrom ?? '')} - ${formatDate(
                patients.pharmaRecommendationDateTo ?? ''
              )}`,
            },
            {
              name: [
                'pharmaRecommendationFollowUpDateFrom',
                'pharmaRecommendationFollowUpDateTo',
                'pharmaRecommendationFollowUpFloatingShortcutName',
              ],
              label: t('filter.patient.pharmaRecommendationFollowUpDate'),
              value: `${formatDate(patients.pharmaRecommendationFollowUpDateFrom ?? '')} - ${formatDate(
                patients.pharmaRecommendationFollowUpDateTo ?? ''
              )}`,
            },
          ]
        : [
            {
              name: 'riskSeverities',
              label: t('filter.patient.riskSeverity'),
              value: (patients.riskSeverities ?? []).map((v) => tEnum('RiskSeverityEnum', v)).join(', '),
            },
            {
              name: 'evaluationStates',
              label: t('filter.patient.checkState'),
              value: (patients.evaluationStates ?? []).map((v) => tEnum('EvaluationStateEnum', v)).join(', '),
            },
            {
              name: ['evaluationStateModifiedFrom', 'evaluationStateModifiedTo'],
              label: t('filter.patient.checkDate'),
              value: `${formatDate(patients.evaluationStateModifiedFrom ?? '')} - ${formatDate(patients.evaluationStateModifiedTo ?? '')}`,
            },
          ]),
      {
        name: 'hospitalizationStates',
        label: t('filter.patient.hospitalizations'),
        value: (patients.hospitalizationStates ?? []).map((v) => tEnum('HospitalizationStateEnum', v)).join(', '),
      },
      {
        name: 'commentSearchString',
        label: t('filter.patient.commentSearchString'),
        value: patients.commentSearchString,
      },
    ]),
    ...withTab('medication', [
      ...ATC_SLOT_NAMES.map((name, index) => ({
        name,
        label: t(ATC_SLOT_LABELS[index]),
        value: (patients[name] ?? []).map(getAtcLabel).join(', '),
      })),
      ...ACTIVE_INGREDIENT_SLOT_NAMES.map((name, index) => ({
        name,
        label: t(ACTIVE_INGREDIENT_SLOT_LABELS[index]),
        value: (patients[name] ?? []).map(getActiveIngredientLabel).join(', '),
      })),
      {
        name: ['atcHierarchyFrom', 'atcHierarchyTo', 'atcHierarchyFloatingShortcutName'],
        label: t('filter.patient.atcHierarchyDate'),
        value: `${formatDate(patients.atcHierarchyFrom ?? '')} - ${formatDate(patients.atcHierarchyTo ?? '')}`,
      },
      {
        name: 'atcHierarchyMatch',
        label: t('filter.patient.atcHierarchyMatch'),
        value: tEnum('AtcHierarchyFilterMatchEnum', patients.atcHierarchyMatch),
        defaultValue: DEFAULT_PATIENTS_FILTERS.atcHierarchyMatch,
      },
    ]),
    ...withTab('laboratory', [
      ...BIOCHEMISTRY_ROWS.map((row) => ({
        // Removing the chip has to clear the whole row, not just one of its three parts.
        name: [row.method, row.operator, row.value],
        label: t(row.label),
        value: getBiochemistryConditionLabel(patients, row, formatters, getLabMethodLabel),
      })),
      {
        name: ['biochemistryFrom', 'biochemistryTo', 'biochemistryFloatingShortcutName'],
        label: t('filter.patient.biochemistryDate'),
        value: `${formatDate(patients.biochemistryFrom ?? '')} - ${formatDate(patients.biochemistryTo ?? '')}`,
      },
      {
        name: 'biochemistryMatch',
        label: t('filter.patient.biochemistryMatch'),
        value: tEnum('AtcHierarchyFilterMatchEnum', patients.biochemistryMatch),
        defaultValue: DEFAULT_PATIENTS_FILTERS.biochemistryMatch,
      },
    ]),
    ...withTab('microbiology', [
      // A row is two chips, the way the design draws it: the pathogens and the resistances can be cleared separately.
      ...MICROBIOLOGY_ROWS.flatMap((row) => [
        {
          name: row.pathogens,
          label: t(row.pathogensLabel),
          value: (patients[row.pathogens] ?? []).map(getPathogenLabel).join(', '),
        },
        {
          name: row.resistances,
          label: t(row.resistancesLabel),
          value: (patients[row.resistances] ?? []).map(getResistanceLabel).join(', '),
        },
      ]),
      {
        name: ['microbiologyFrom', 'microbiologyTo', 'microbiologyFloatingShortcutName'],
        label: t('filter.patient.microbiologyDate'),
        value: `${formatDate(patients.microbiologyFrom ?? '')} - ${formatDate(patients.microbiologyTo ?? '')}`,
      },
      {
        name: 'microbiologyMatch',
        label: t('filter.patient.microbiologyMatch'),
        value: tEnum('AtcHierarchyFilterMatchEnum', patients.microbiologyMatch),
        defaultValue: DEFAULT_PATIENTS_FILTERS.microbiologyMatch,
      },
    ]),
    ...withTab('other', [
      {
        name: 'icdCodes',
        label: t('filter.patient.icdCodes'),
        value: (patients.icdCodes ?? []).map(getIcdLabel).join(', '),
      },
      {
        name: ['ageFrom', 'ageTo'],
        label: t('filter.patient.age'),
        value: `${patients.ageFrom ?? ''} - ${patients.ageTo ?? ''}`,
      },
      {
        name: ['weightFrom', 'weightTo'],
        label: t('filter.patient.weight'),
        value: `${patients.weightFrom ?? ''} - ${patients.weightTo ?? ''}`,
      },
      {
        name: ['bmiFrom', 'bmiTo'],
        label: t('filter.patient.bmi'),
        value: `${patients.bmiFrom ?? ''} - ${patients.bmiTo ?? ''}`,
      },
      {
        name: 'deathDuringHospitalizations',
        label: t('filter.patient.deathDuringHospitalization'),
        value: (patients.deathDuringHospitalizations ?? []).map((v) => tEnum('DeathDuringHospitalizationEnum', v)).join(', '),
      },
    ]),
  ];
}

/** The chips of the hospitalizations filters, memoized over the filter and the lookups. */
export function useHospitalizedPatientsSelectedFilters(
  patients: PatientsFilters,
  formatters: Formatters,
  lookups: HospitalizedPatientsChipLookups
): SelectedFilter[] {
  return useMemo(() => buildHospitalizedPatientsSelectedFilters(patients, formatters, lookups), [patients, formatters, lookups]);
}
